import { Link } from "react-router-dom";
import { Card } from "@volca/ui/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { Switch } from "@volca/ui/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@volca/ui/components/ui/table";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { PAYROLL_SETTINGS_ROSTER_PATH } from "@/payroll/settings/routes";
import { SheetNameField } from "./sheet-name-field";
import type {
  AccessKeySetting,
  WorkbookAccessSettings,
  WorkingAccess,
} from "./types";
import { sheetHolderIds } from "./utils";

interface TeamsTableProps {
  settings: WorkbookAccessSettings;
  working: WorkingAccess;
  saving: boolean;
  // Resolves true once the change is saved.
  onChange: (setting: AccessKeySetting) => Promise<boolean>;
}

export const TeamsTable = ({
  settings,
  working,
  saving,
  onChange,
}: TeamsTableProps) => {
  const userName = new Map(settings.users.map((user) => [user.id, user.name]));

  if (settings.access_keys.length === 0) {
    return (
      <Empty className={emptyStateVariants({ layout: "inline" })}>
        <EmptyHeader>
          <EmptyDescription>
            Your workbook has no sheets yet. Your Volca contact sets them up
            with your pay plan. Until then, access is given to the whole
            workbook.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <Stack className="w-full" gap={2} align="stretch">
      <Text tone="muted">
        Each sheet covers tabs of the workbook. Make a sheet a team to also
        limit whose pay people see: someone on a team only sees the technicians
        the roster puts on that team, unless they have the whole workbook.
      </Text>
      <Card className="gap-0 py-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Sheet</TableHead>
              <TableHead>Workbook tabs</TableHead>
              <TableHead>People with access</TableHead>
              <TableHead>Team</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {settings.access_keys.map((area) => {
              const setting = working.keySettings.find(
                (s) => s.key === area.key,
              );
              if (!setting) return null;
              const holders = sheetHolderIds(working.assignments, area.key).map(
                (id) => userName.get(id) ?? id,
              );
              return (
                <TableRow key={area.key}>
                  <TableCell className="min-w-56 py-3">
                    <SheetNameField
                      id={`sheet-name-${area.key}`}
                      label={`Name for ${area.name}`}
                      name={setting.name}
                      disabled={saving}
                      onCommit={(name) => onChange({ ...setting, name })}
                    />
                  </TableCell>
                  <TableCell className="py-3 whitespace-normal">
                    <Text tone={area.sheets.length ? "default" : "muted"}>
                      {area.sheets.length
                        ? area.sheets.join(", ")
                        : "No tabs in the latest workbook"}
                    </Text>
                  </TableCell>
                  <TableCell className="py-3 whitespace-normal">
                    <Text tone={holders.length ? "default" : "muted"}>
                      {holders.length ? holders.join(", ") : "Nobody yet"}
                    </Text>
                  </TableCell>
                  <TableCell className="py-3 whitespace-normal">
                    <Stack align="start" gap={1}>
                      <Stack direction="row" gap={2} align="center">
                        <Switch
                          aria-label={`Use ${area.name} as a team`}
                          checked={setting.team_eligible}
                          disabled={saving}
                          onCheckedChange={(checked) =>
                            void onChange({
                              ...setting,
                              team_eligible: checked,
                            })
                          }
                        />
                        <Text
                          tone={setting.team_eligible ? "default" : "muted"}
                          className="whitespace-nowrap"
                        >
                          {setting.team_eligible ? "Team" : "Not a team"}
                        </Text>
                      </Stack>
                      {area.team_eligible && (
                        <Text variant="caption" tone="muted">
                          {area.team_worker_count}{" "}
                          {area.team_worker_count === 1
                            ? "technician"
                            : "technicians"}{" "}
                          on this team.{" "}
                          <Link
                            className="text-link underline"
                            to={PAYROLL_SETTINGS_ROSTER_PATH}
                          >
                            Edit in roster
                          </Link>
                        </Text>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </Stack>
  );
};
